<script setup lang="ts">
/* =============================================================
   BERTH — DETAIL
   =============================================================
   Every field on the row, and the only place a berth can be
   edited or deleted from.

   That is deliberate: a delete button on a list row is one
   mis-aimed click away from destroying something you could only
   see three fields of. Here you are looking at the whole record
   when you decide.
   ============================================================= */

import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import DetailList from '@/components/ui/DetailList.vue'
import type { DetailRow } from '@/components/ui/DetailList.vue'
import { routes, to } from '@/config/routes'
import { deleteBerth, getBerth } from '@/lib/berths'
import { useCountries } from '@/lib/countries'
import { useAmenities } from '@/lib/amenities'
import { DataError } from '@/lib/errors'
import Tabs from '@/components/ui/Tabs.vue'
import type { TabItem } from '@/components/ui/Tabs.vue'
import BerthRatesPanel from '@/components/BerthRatesPanel.vue'
import BerthBlocksPanel from '@/components/BerthBlocksPanel.vue'
import BookingsPanel from '@/components/BookingsPanel.vue'
import { bookingModeLabel } from '@/lib/bookingLabels'
import type { Berth } from '@/lib/types'

const route = useRoute()
const router = useRouter()
const { countryName } = useCountries()
const { amenityLabel } = useAmenities()

const berth = ref<Berth | null>(null)
/** Filled in by the bookings tab once it has loaded, for the tab's count. */
const bookingCount = ref<number | null>(null)
const loading = ref(true)
const loadError = ref('')

const id = computed(() => Number(route.params.id))

/* --- tabs -----------------------------------------------------------------
   Details, rates, availability and bookings were one long scroll; on a phone
   the bookings were four screens down. The tab lives in the query string so
   a link can open straight on it and Back steps between tabs. `replace`
   rather than push would lose that, so it is a push. */

const TAB_KEYS = ['details', 'rates', 'availability', 'bookings'] as const
type TabKey = (typeof TAB_KEYS)[number]

const tab = computed<TabKey>({
  get: () => {
    const q = route.query.tab
    return TAB_KEYS.includes(q as TabKey) ? (q as TabKey) : 'details'
  },
  set: (key) => {
    void router.push({ query: { ...route.query, tab: key === 'details' ? undefined : key } })
  },
})

const tabs = computed<TabItem[]>(() => [
  { key: 'details', label: 'Details' },
  { key: 'rates', label: 'Rates' },
  { key: 'availability', label: 'Availability' },
  { key: 'bookings', label: 'Bookings', count: bookingCount.value },
])

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    berth.value = await getBerth(id.value)
  } catch (e) {
    loadError.value = e instanceof DataError ? e.message : 'Could not load the berth.'
  } finally {
    loading.value = false
  }
}

onMounted(load)
// Navigating between two detail pages reuses this component, so the id can
// change without a remount.
watch(id, load)

const rows = computed<DetailRow[]>(() => {
  const b = berth.value
  if (!b) return []
  return [
    { label: 'Name', value: b.name },
    // The embed is null only when the reader cannot see `companies` — see
    // the note on Berth.company. A signed-in admin always gets a name.
    b.company
      ? { label: 'Owner', value: b.company.name, to: to.company(b.company.id) }
      : { label: 'Owner', value: String(b.company_id), mono: true },
    { label: 'City', value: b.city },
    { label: 'Country', value: `${countryName(b.country_code)} (${b.country_code})` },
    // `empty: true` renders an em dash. An empty array is a real answer here
    // — the berth offers none of the five — not a missing value.
    b.amenities.length > 0
      ? { label: 'Amenities', tags: b.amenities.map(amenityLabel) }
      : { label: 'Amenities', empty: true },
    // "Not stated" rather than a fabricated limit — the column is nullable so
    // that the difference survives; see the capacity migration.
    b.max_length_m === null
      ? { label: 'Max vessel length', empty: true }
      : { label: 'Max vessel length', value: `${b.max_length_m} m`, mono: true },
    b.max_vessels === null
      ? { label: 'Max vessels at once', empty: true }
      : { label: 'Max vessels at once', value: String(b.max_vessels), mono: true },
    {
      label: 'Booking mode',
      value:
        b.booking_mode === 'manual' && b.response_hours !== null
          ? `${bookingModeLabel(b.booking_mode)} · usually answers within ${b.response_hours} h`
          : bookingModeLabel(b.booking_mode),
    },
    { label: 'Latitude', value: String(b.latitude), mono: true },
    { label: 'Longitude', value: String(b.longitude), mono: true },
    { label: 'ID', value: String(b.id), mono: true },
    { label: 'Created', value: new Date(b.created_at).toLocaleString('en-GB') },
  ]
})

/* --- delete ------------------------------------------------------------- */

const confirming = ref(false)
const confirm = useTemplateRef<InstanceType<typeof ConfirmDialog>>('confirm')

async function onDeleteConfirmed(): Promise<void> {
  const b = berth.value
  if (!b) return
  try {
    await deleteBerth(b.id)
    confirming.value = false
    await router.push(routes.berths)
  } catch (e) {
    confirm.value?.fail(e instanceof DataError ? e.message : 'Could not delete the berth.')
  }
}
</script>

<template>
  <p v-if="loading" class="page-status">Loading…</p>

  <template v-else-if="berth">
    <PageHeader :title="berth.name" :description="`${berth.city}, ${countryName(berth.country_code)}`">
      <template #actions>
        <Button variant="ghost" icon="chevronRight" :to="routes.berths" class="back-link">
          All berths
        </Button>
        <Button variant="secondary" icon="edit" :to="to.berthEdit(berth.id)">Edit</Button>
        <Button variant="danger" icon="trash" @click="confirming = true">Delete</Button>
      </template>
    </PageHeader>

    <Tabs v-model="tab" :tabs="tabs" label="Berth sections" />

    <div :id="`tabpanel-${tab}`" role="tabpanel" :aria-labelledby="`tab-${tab}`">
      <Panel v-if="tab === 'details'">
        <DetailList :rows="rows" />
      </Panel>

      <BerthRatesPanel v-else-if="tab === 'rates'" :berth-id="berth.id" />

      <BerthBlocksPanel v-else-if="tab === 'availability'" :berth-id="berth.id" />

      <BookingsPanel
        v-else
        :filter="{ berthId: berth.id }"
        :hide="['berth']"
        description="At this berth, newest arrival first."
        @loaded="bookingCount = $event"
      />
    </div>

    <ConfirmDialog
      ref="confirm"
      :open="confirming"
      title="Delete berth"
      :message="`“${berth.name}” will be permanently removed. This cannot be undone.`"
      @close="confirming = false"
      @confirmed="onDeleteConfirmed"
    />
  </template>

  <template v-else>
    <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>
    <EmptyState
      v-else
      icon="anchor"
      title="Berth not found"
      description="It may have been deleted, or the link may be wrong."
    >
      <template #action>
        <Button :to="routes.berths">All berths</Button>
      </template>
    </EmptyState>
  </template>
</template>

